import { InertiaLink } from '@inertiajs/inertia-react';
import Layout from 'app/admin/Layout';
import React, { useContext } from 'react';
import WhiteContainer from 'app/admin/WhiteContainer';
import Button from 'app/components/Buttons/Button';
import Icon from 'app/components/Icon';
import route from 'ziggy-js';
import { ModalsContext, MODALS } from 'app/components/Layouts/Modals';
import { defaultButtons } from 'app/admin/ConfirmModal';

interface Inquiry {
    id: number;
    event_type: 'cafe' | 'custom';
    package_type?: 'exclusive' | 'standard' | 'basic' | 'custom';
    layout?: 'rozvrzeni1' | 'rozvrzeni2';
    person_count: number;
    menu_custom?: any;
    /*   menu_custom_total_price?: number; */
    menu_total_price?: number;
    total_price?: number;
    price_per_person?: number;
    street?: string;
    zip?: string;
    city?: string;
    company?: string;
    name: string;
    surname: string;
    phone: string;
    email: string;
    request?: string;
    created_at: string;
}

interface Props {
    inquiry: Inquiry;
}

function Show({ inquiry }: Props) {
    // Rental discount eligibility: 50% off when total menu price >= 23 100 Kč
    const discountThreshold = 23100;
    const totalMenuPrice = inquiry.menu_total_price ?? 0;
    const hasRentalDiscount = totalMenuPrice >= discountThreshold;
    const { open, close } = useContext(ModalsContext);

    const removeInquiry = (e: React.MouseEvent<HTMLAnchorElement> | React.KeyboardEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        open(MODALS.CONFIRM, false, {
            title: 'Potvrdit smazání',
            message: 'Opravdu chcete smazat poptávku?',
            buttons: defaultButtons(
                route('admin.catering.destroy', { inquiry: inquiry.id }),
                () => {
                    close();
                },
                close
            ),
        });
    };

    const formatEventType = (type: string) => type === 'cafe' ? 'Kavárna' : 'Vlastní';
    const formatPackageType = (type?: string) => {
        if (!type) return 'Neuvedeno';
        switch (type) {
            case 'exclusive': return 'Exkluzivní';
            case 'standard': return 'Standardní';
            case 'basic': return 'Základní';
            case 'custom': return 'Vlastní';
            default: return type;
        }
    };
    const formatLayout = (layout?: string) => layout ? (layout === 'rozvrzeni1' ? 'Rozvržení 1' : 'Rozvržení 2') : 'Neuvedeno';
    const formatPrice = (price?: number) => price ? `${price} Kč` : 'Neuvedeno';
    const formatAddress = () => {
        const parts = [inquiry.street, inquiry.zip, inquiry.city].filter(Boolean);
        return parts.length > 0 ? parts.join(', ') : 'Neuvedeno';
    };

    return (
        <Layout>
            <div className="pt-40px pb-130px">
                <div className="flex justify-between items-center gap-32px">
                    <div className="text-h32 font-semibold flex-shrink-0">Detail poptávky</div>
                    <div className="flex gap-16px">
                        <InertiaLink href="/">
                            <Button href={'/admin/catering'}>
                                <Icon className="pr-16px" name="arrow-left" />
                                Zpět na seznam
                            </Button>
                        </InertiaLink>
                        {/* <InertiaLink href="/">
                            <Button href={'/admin/catering/' + inquiry.id + '/edit'}>
                                <Icon className="pr-16px" name="pencil" />
                                Upravit
                            </Button>
                        </InertiaLink> */}
                        {/* <Button onClick={removeInquiry} className="text-red-600 hover:text-red-800" href={'/'}>
                            <Icon className="pr-16px" name="trash" />
                            Smazat
                        </Button> */}
                    </div>
                </div>

                <WhiteContainer className="mt-40px">
                    <div className="grid grid-cols-2 gap-32px">
                        {/* Základní informace */}
                        <div>
                            <h3 className="text-h font-semibold text-[18px] leading-[24px] mr-8px mb-8px">Základní informace</h3>
                            <div className="space-y-8px">
                                <div className="flex">
                                    <span className="w-150px font-bold mr-8px text-black">Jméno:</span>
                                    <span>{`${inquiry.name} ${inquiry.surname}`}</span>
                                </div>
                                <div className="flex">
                                    <span className="w-150px font-bold mr-8px text-black">Email:</span>
                                    <span>{inquiry.email}</span>
                                </div>
                                <div className="flex">
                                    <span className="w-150px font-bold mr-8px text-black">Telefon:</span>
                                    <span>{inquiry.phone}</span>
                                </div>
                                <div className="flex">
                                    <span className="w-150px font-bold mr-8px text-black">Firma:</span>
                                    <span>{inquiry.company || <span className="italic text-red opacity-80">Neuvedeno</span>}</span>
                                </div>
                                <div className="flex">
                                    <span className="w-150px font-bold mr-8px text-black">Datum vytvoření:</span>
                                    <span>{new Date(inquiry.created_at).toLocaleDateString('cs-CZ')}</span>
                                </div>
                            </div>
                        </div>

                        {/* Detaily události */}
                        <div>
                            <h3 className="text-h24 text-[18px] leading-[24px] font-bold mb-8px">Detaily události</h3>
                            <div className="space-y-8px">
                                <div className="flex">
                                    <span className="w-150px mr-8px font-bold text-black">Typ události:</span>
                                    <span>{formatEventType(inquiry.event_type)}</span>
                                </div>
                                {/* Zobrazit typ balíčku jen pokud není "custom" */}
                                {inquiry.package_type !== 'custom' && (
                                    <div className="flex">
                                        <span className="w-150px mr-8px font-bold text-black">Typ balíčku:</span>
                                        <span>{formatPackageType(inquiry.package_type)}</span>
                                    </div>
                                )}
                                <div className="flex">
                                    <span className="w-150px mr-8px font-bold text-black">Rozvržení:</span>
                                    <span>{inquiry.layout || <span className="italic text-red opacity-80">Neuvedeno</span>}</span>
                                </div>
                                <div className="flex">
                                    <span className="w-150px mr-8px font-bold text-black">Počet osob:</span>
                                    <span>{inquiry.person_count}</span>
                                </div>
                            </div>
                        </div>

                        {/* Adresa */}
                        <div>
                            <h3 className="text-h24 text-[18px] leading-[24px] font-bold mb-8px">Adresa</h3>
                            <div className="space-y-8px">
                                <div className="flex">
                                    <span className="w-150px mr-8px font-bold text-black">Adresa:</span>
                                    <span>{formatAddress()}</span>
                                </div>
                            </div>
                        </div>

                        {/* Finanční informace */}
                        <div>
                            <h3 className="text-h24 font-bold text-[18px] leading-[24px] mb-8px">Finanční informace</h3>
                            <div className="space-y-8px">
                                <div className="flex">
                                    <span className="w-150px mr-8px font-bold text-black">Celková cena (vč. DPH):</span>
                                    <span>{formatPrice(inquiry.total_price)}</span>
                                </div>
                                <div className="flex">
                                    <span className="w-150px mr-8px font-bold text-black">Celková cena (bez DPH):</span>
                                    <span>{formatPrice(inquiry.total_price / 1.25)}</span>
                                </div>
                                <div className="flex">
                                    <span className="w-150px mr-8px font-bold text-black">Cena za osobu:</span>
                                    <span>{formatPrice(inquiry.price_per_person)}</span>
                                </div>
                                <div className="flex">
                                    <span className="w-150px mr-8px font-bold text-black">Sleva na pronájem:</span>
                                    <span>{hasRentalDiscount ? 'Ano' : 'Ne'}</span>
                                </div>
                            </div>
                        </div>

                        {/* Vlastní menu – zobrazuje se jen u custom balíčku */}
                        {inquiry.package_type === 'custom' && (
                            <div className="col-span-2">
                                <h3 className="text-h24 text-[18px] leading-[24px] font-bold mb-8px">Vlastní menu</h3>
                                <div className="space-y-8px">
                                    {inquiry.menu_custom ? (
                                        <div className="space-y-16px">
                                            {/*   {inquiry.menu_custom.note && (
                                                <p className="text-16 mb-8px font-medium">{inquiry.menu_custom.note}</p>
                                            )} */}
                                            {inquiry.menu_custom.sections.map((section, sectionIndex) => (
                                                <div key={sectionIndex} className="space-y-8px">
                                                    {section.title && (
                                                        <h4 className="text-18 font-semibold">{section.title}</h4>
                                                    )}
                                                    {section.description && (
                                                        <p className="text-14   text-black">{section.description}</p>
                                                    )}
                                                    <ul className="list-disc list-inside ml-24px text-black">
                                                        {section.items.map((item, itemIndex) => (
                                                            <li key={itemIndex}>
                                                                {item.count ?? item.defaultCount}x {item.name}
                                                                {item.price && (
                                                                    <span className="font-bold"> – {item.price} Kč</span>
                                                                )}
                                                            </li>
                                                        ))}
                                                    </ul>

                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <span className="italic text-red mr-8px opacity-80">Neuvedeno</span>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Další požadavky */}
                        <div className="col-span-2">
                            <h3 className="text-h24 text-[18px] leading-[24px] font-bold mb-8px">Další požadavky</h3>
                            <div className="space-y-8px">
                                <div className="flex">
                                    <span className="w-150px mr-8px font-bold text-black">Požadavky:</span>
                                    <span>{inquiry.request || <span className="italic text-red ml-8px opacity-80">Neuvedeno</span>}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </WhiteContainer>
            </div>
        </Layout>
    );
}

export default Show;